/**
 * AppleSignInButton — "Apple ile Giriş Yap" (yalnızca iOS uygulaması).
 *
 * NEDEN VAR: App Store 4.8 kuralı, üçüncü taraf giriş (Google) sunan
 * uygulamalardan eşdeğer gizlilik odaklı bir seçenek ister. Bu düğme o
 * seçenek. Web ve Android'de ÇİZİLMEZ: kural iOS uygulamasını bağlar,
 * Apple'ın web akışı ise ayrı bir Services ID + alan doğrulaması gerektirir
 * ve o kurulum yapılmadan düğmeyi göstermek çalışmayan bir tuş demek olurdu.
 *
 * Sunucuya YALNIZCA Apple'ın imzaladığı kimlik jetonu gider; sunucu imzayı
 * Apple'ın açık anahtarlarıyla doğrular. İstemcinin söylediği e-postaya
 * güvenilmez.
 *
 * Apple'a özgü iki davranış (kod buna göre yazıldı):
 *   • Ad SADECE ilk girişte gelir — o yüzden jetonla birlikte gönderiyoruz,
 *     sunucu ilk kayıtta saklıyor. Sonraki girişlerde boş gelmesi normaldir.
 *   • Kullanıcı e-postasını gizleyebilir (@privaterelay.appleid.com); hesap
 *     yine benzersizdir, kimlik jetonun `sub` alanıdır.
 */
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../auth";
import { isCapacitorNative } from "../lib/pwaCamera";
import { Loader2 } from "lucide-react";

/** Paketlenmiş iOS uygulamasının içinde miyiz? (iPhone'daki Safari değil.) */
function isIOSNative() {
  if (typeof window === "undefined") return false;
  try {
    return isCapacitorNative() && window.Capacitor?.getPlatform?.() === "ios";
  } catch {
    return false;
  }
}

/** Düğme gerçekten çizilecek mi? Sayfalar "veya" ayracını buna göre gösterir. */
export function isAppleSignInAvailable() {
  if (!isIOSNative()) return false;
  try {
    return typeof window.Capacitor?.Plugins?.SocialLogin?.login === "function";
  } catch {
    return false;
  }
}

export default function AppleSignInButton({ isTr = true, onSuccess = null }) {
  const { loginWithApple } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!isAppleSignInAvailable()) return null;

  const gir = async () => {
    setBusy(true);
    try {
      const SocialLogin = window.Capacitor?.Plugins?.SocialLogin;
      await SocialLogin.initialize({
        apple: { clientId: "tr.sokakkedileri.app" },
      });
      const res = await SocialLogin.login({
        provider: "apple",
        options: { scopes: ["email", "name"] },
      });

      const r = res?.result || {};
      const identityToken = r.idToken || r.identityToken;
      if (!identityToken) throw new Error("no identity token");

      const kullanici = await loginWithApple({
        identity_token: identityToken,
        given_name: r.profile?.givenName || r.givenName || "",
        family_name: r.profile?.familyName || r.familyName || "",
      });
      onSuccess?.(kullanici);
    } catch (err) {
      // Kullanıcı vazgeçtiyse uyarı basmıyoruz — hata değil, karar.
      const msg = String(err?.message || err || "");
      if (!/cancel|closed|dismiss|1001/i.test(msg)) {
        const detail = err?.response?.data?.detail;
        toast.error(
          (typeof detail === "string" && detail) ||
            (isTr ? "Apple ile giriş yapılamadı." : "Could not sign in with Apple."),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={gir}
      disabled={busy}
      data-testid="apple-signin-btn"
      // Apple'ın marka kuralı: siyah zemin, beyaz logo ve metin, tam genişlik.
      className="w-full h-11 rounded-xl bg-black text-white font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.99] transition-transform disabled:opacity-60"
    >
      {busy ? (
        <Loader2 size={16} className="animate-spin" />
      ) : (
        // Elmanın kendi çizim alanına SIĞMASI şart. Önceki yolda gövdenin sol
        // kenarı x = -1.2'ye taşıyordu ama viewBox 0'dan başlıyordu: glifin
        // solu kırpılıyor, elma kutusunun içinde sağa kaymış görünüyordu.
        // Bu yol 384×512'lik alanın içinde her yönden eşit boşlukla duruyor,
        // yani ortalama işini artık kaydırma hilesi değil geometri yapıyor.
        // preserveAspectRatio varsayılanı (xMidYMid meet) oranı korur.
        <svg
          width="14"
          height="18"
          viewBox="0 0 384 512"
          fill="currentColor"
          aria-hidden
        >
          <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
        </svg>
      )}
      {isTr ? "Apple ile Giriş Yap" : "Sign in with Apple"}
    </button>
  );
}
